import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Landmark, Receipt, HeartHandshake, CalendarRange, AlertTriangle, Mail, BellRing, Trash2, RefreshCw } from "lucide-react";
import api, { getErr } from "../../lib/api";
import { NeonButton, NeonCard, Spinner, Pill } from "../../components/Neon";
import { gbp, fmtDateTime } from "../../lib/format";

const PERIODS = ["today", "week", "month", "quarter", "year", "lifetime"];
const Stat = ({ label, value, color = "white" }) => (
  <div className="rounded-xl border border-white/10 bg-[var(--surface-2)] px-4 py-3">
    <p className="text-[11px] uppercase tracking-widest text-[var(--text-muted)]">{label}</p>
    <p className={`mt-1 font-display text-2xl ${color === "pink" ? "text-[var(--neon-pink)]" : color === "green" ? "text-[var(--neon-green)]" : color === "gold" ? "text-[var(--neon-gold)]" : color === "red" ? "text-[var(--error,#ff4d6d)]" : "text-white"}`}>{value}</p>
  </div>
);

export default function AdminFinance() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");
  const [resetScope, setResetScope] = useState("transactions");
  const [resetConfirm, setResetConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/vat").then((r) => r.data).catch(() => null),
      api.get("/admin/tax-estimate").then((r) => r.data).catch(() => null),
      api.get("/admin/charity").then((r) => r.data).catch(() => null),
      api.get("/admin/monthly-statement").then((r) => r.data).catch(() => null),
      api.get("/admin/loss-warnings").then((r) => r.data).catch(() => null),
      api.get("/admin/email-health").then((r) => r.data).catch(() => null),
      api.get("/admin/high-value-alerts").then((r) => r.data).catch(() => []),
    ]).then(([vat, tax, charity, monthly, loss, email, alerts]) => {
      setData({ vat, tax, charity, monthly, loss, email, alerts: alerts || [] });
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const ackAlert = async (id) => {
    try { await api.post(`/admin/high-value-alerts/${id}/ack`); toast.success("Alert acknowledged."); load(); }
    catch (e) { toast.error(getErr(e)); }
  };

  const doReset = async () => {
    if (resetConfirm !== "RESET") { toast.error('Type RESET to confirm.'); return; }
    if (!window.confirm(`This wipes the "${resetScope}" ledger to £0 (competitions & users are kept). This cannot be undone. Continue?`)) return;
    setBusy(true);
    try { await api.post("/admin/bookkeeping/reset", { scope: resetScope, confirm: "RESET" }); toast.success(`Bookkeeping reset: ${resetScope}.`); setResetConfirm(""); load(); }
    catch (e) { toast.error(getErr(e)); } finally { setBusy(false); }
  };

  if (loading) return <Spinner />;
  const { vat, tax, charity, monthly, loss, email, alerts } = data || {};
  const vp = vat?.periods?.[period] || {};
  const cp = charity?.periods?.[period] || {};

  return (
    <div data-testid="admin-finance" className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display uppercase text-2xl text-white">Finance &amp; Tax</h2>
        <NeonButton variant="ghost" onClick={load} data-testid="finance-refresh"><RefreshCw className="h-4 w-4" /> Refresh</NeonButton>
      </div>

      {/* Period selector */}
      <div className="flex flex-wrap gap-2" data-testid="finance-period">
        {PERIODS.map((p) => (
          <button key={p} onClick={() => setPeriod(p)} data-testid={`period-${p}`}
            className={`rounded-full px-3.5 py-1.5 text-xs font-display uppercase tracking-wide transition-colors ${period === p ? "bg-[var(--neon-cyan)] text-black" : "border border-white/15 text-[var(--text-muted)] hover:text-white"}`}>{p}</button>
        ))}
      </div>

      {/* VAT */}
      <NeonCard variant="flow" innerClassName="p-5">
        <div className="flex items-center gap-2 text-[var(--neon-cyan)]"><Landmark className="h-4 w-4" /><span className="font-display uppercase tracking-widest text-sm">VAT Monitor ({vat?.vat_rate_pct ?? 20}% · {period})</span></div>
        <div className="mt-3 grid sm:grid-cols-4 gap-3">
          <Stat label="Gross sales" value={gbp(vp.gross_sales || 0)} />
          <Stat label="Net (ex VAT)" value={gbp(vp.net_ex_vat || 0)} />
          <Stat label="VAT due" value={gbp(vp.vat_due || 0)} color="gold" />
          <Stat label="Transactions" value={vp.transactions || 0} />
        </div>
        <p className="mt-2 text-xs text-[var(--text-muted)]">Registration threshold {gbp(vat?.vat_registration_threshold || 90000)}. {vat?.disclaimer}</p>
      </NeonCard>

      {/* Tax estimate */}
      <NeonCard variant="soft" innerClassName="p-5">
        <div className="flex items-center gap-2 text-[var(--neon-green)]"><Receipt className="h-4 w-4" /><span className="font-display uppercase tracking-widest text-sm">UK Tax Estimator</span></div>
        <p className="mt-2 text-sm text-[var(--text-muted)]">On net profit <span className="text-white">{gbp(tax?.net_profit || 0)}</span></p>
        <div className="mt-3 grid sm:grid-cols-2 gap-4">
          <div className="rounded-xl border border-white/10 p-4">
            <p className="font-display uppercase text-white">Sole trader</p>
            <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
              <span className="text-[var(--text-muted)]">Income tax</span><span className="text-right text-white">{gbp(tax?.sole_trader?.income_tax || 0)}</span>
              <span className="text-[var(--text-muted)]">Class 4 NI</span><span className="text-right text-white">{gbp(tax?.sole_trader?.class4_ni || 0)}</span>
              <span className="text-[var(--text-muted)]">Total due</span><span className="text-right text-[var(--neon-gold)]">{gbp(tax?.sole_trader?.total || 0)}</span>
              <span className="text-[var(--text-muted)]">Take-home</span><span className="text-right text-[var(--neon-green)]">{gbp(tax?.sole_trader?.take_home || 0)}</span>
            </div>
          </div>
          <div className="rounded-xl border border-white/10 p-4">
            <p className="font-display uppercase text-white">Ltd company</p>
            <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
              <span className="text-[var(--text-muted)]">Corporation tax</span><span className="text-right text-[var(--neon-gold)]">{gbp(tax?.ltd_company?.corporation_tax || 0)}</span>
              <span className="text-[var(--text-muted)]">Retained</span><span className="text-right text-[var(--neon-green)]">{gbp(tax?.ltd_company?.retained || 0)}</span>
            </div>
          </div>
        </div>
        <p className="mt-2 text-xs text-[var(--text-muted)]">{tax?.disclaimer}</p>
      </NeonCard>

      {/* Charity */}
      <NeonCard variant="soft" innerClassName="p-5">
        <div className="flex items-center gap-2 text-[var(--neon-pink)]"><HeartHandshake className="h-4 w-4" /><span className="font-display uppercase tracking-widest text-sm">Charity — {charity?.charity_name}</span></div>
        <div className="mt-3 grid sm:grid-cols-4 gap-3">
          <Stat label={`Raised (${period})`} value={gbp(cp.raised || 0)} color="pink" />
          <Stat label={`Donations (${period})`} value={cp.donations || 0} />
          <Stat label="Total raised" value={gbp(charity?.total_raised || 0)} color="pink" />
          <Stat label="Unique donors" value={charity?.unique_donors || 0} />
        </div>
        {(charity?.recent || []).length > 0 && (
          <div className="mt-3 text-sm text-[var(--text-muted)]">Recent: {(charity.recent || []).slice(0, 5).map((r, i) => <span key={i} className="mr-3">{gbp(r.amount)}</span>)}</div>
        )}
      </NeonCard>

      {/* Monthly statement */}
      <NeonCard variant="soft" innerClassName="p-0">
        <div className="flex items-center gap-2 text-white p-4 pb-0"><CalendarRange className="h-4 w-4 text-[var(--neon-cyan)]" /><span className="font-display uppercase tracking-widest text-sm">Monthly Statement (12 mo)</span></div>
        <div className="overflow-x-auto p-4"><table className="w-full text-sm">
          <thead><tr className="text-left text-[var(--text-muted)] border-b border-white/10"><th className="py-2">Month</th><th>Revenue</th><th>Expenses</th><th>Charity</th><th>VAT</th><th>Net profit</th></tr></thead>
          <tbody>
            {(monthly?.months || []).map((m) => (
              <tr key={m.month} className="border-b border-white/5">
                <td className="py-2 text-white">{m.month}</td><td className="text-white">{gbp(m.revenue)}</td><td className="text-[var(--text-muted)]">{gbp(m.expenses)}</td>
                <td className="text-[var(--neon-pink)]">{gbp(m.charity)}</td><td className="text-[var(--neon-gold)]">{gbp(m.vat)}</td>
                <td className={m.net_profit < 0 ? "text-[var(--error,#ff4d6d)]" : "text-[var(--neon-green)]"}>{gbp(m.net_profit)}</td>
              </tr>
            ))}
            {monthly?.totals && (
              <tr className="font-display"><td className="py-2 text-white uppercase">Total</td><td className="text-white">{gbp(monthly.totals.revenue)}</td><td className="text-[var(--text-muted)]">{gbp(monthly.totals.expenses)}</td><td className="text-[var(--neon-pink)]">{gbp(monthly.totals.charity)}</td><td className="text-[var(--neon-gold)]">{gbp(monthly.totals.vat)}</td><td className={monthly.totals.net_profit < 0 ? "text-[var(--error,#ff4d6d)]" : "text-[var(--neon-green)]"}>{gbp(monthly.totals.net_profit)}</td></tr>
            )}
          </tbody>
        </table></div>
      </NeonCard>

      {/* Loss warnings + Email health */}
      <div className="grid lg:grid-cols-2 gap-6">
        <NeonCard variant="soft" innerClassName="p-5">
          <div className="flex items-center gap-2 text-[var(--neon-gold)]"><AlertTriangle className="h-4 w-4" /><span className="font-display uppercase tracking-widest text-sm">Loss Warnings ({loss?.at_risk_count || 0})</span></div>
          {(loss?.at_risk || []).length ? (
            <ul className="mt-3 space-y-2">{(loss.at_risk).map((c, i) => (
              <li key={i} className="flex items-center justify-between text-sm border-b border-white/5 pb-2"><span className="text-white line-clamp-1">{c.title}</span><span className="text-[var(--error,#ff4d6d)]">{gbp(c.projected_profit ?? c.projected_pl ?? 0)}</span></li>
            ))}</ul>
          ) : <p className="mt-3 text-sm text-[var(--text-muted)]">No competitions at risk of a loss. 🎉</p>}
        </NeonCard>

        <NeonCard variant="soft" innerClassName="p-5">
          <div className="flex items-center gap-2 text-white"><Mail className="h-4 w-4 text-[var(--neon-cyan)]" /><span className="font-display uppercase tracking-widest text-sm">Email Health</span></div>
          <div className="mt-3 flex items-center gap-2">
            <Pill color={email?.status === "green" ? "green" : email?.status === "amber" ? "gold" : "red"}>{(email?.status || "unknown").toUpperCase()}</Pill>
            <span className="text-sm text-[var(--text-muted)]">{email?.configured ? "Resend configured" : "Provider not configured (mocked)"}</span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <Stat label="Delivered" value={email?.delivered || 0} color="green" />
            <Stat label="Failed" value={email?.failed || 0} color="red" />
          </div>
          {email?.message && <p className="mt-2 text-xs text-[var(--text-muted)]">{email.message}</p>}
        </NeonCard>
      </div>

      {/* High-value alerts */}
      <NeonCard variant="flow" innerClassName="p-5">
        <div className="flex items-center gap-2 text-[var(--neon-pink)]"><BellRing className="h-4 w-4" /><span className="font-display uppercase tracking-widest text-sm">High-Value / AML Alerts ({alerts?.length || 0})</span></div>
        {(alerts || []).length ? (
          <ul className="mt-3 space-y-2" data-testid="hv-alerts">{alerts.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/10 px-3 py-2">
              <div className="text-sm"><span className="text-white">{a.title || a.type || "Alert"}</span> <span className="text-[var(--text-muted)]">{a.detail || a.message || ""}</span></div>
              <NeonButton variant="ghost" onClick={() => ackAlert(a.id)} data-testid={`ack-${a.id}`}>Acknowledge</NeonButton>
            </li>
          ))}</ul>
        ) : <p className="mt-3 text-sm text-[var(--text-muted)]">No open alerts.</p>}
      </NeonCard>

      {/* Bookkeeping reset (destructive) */}
      <NeonCard variant="soft" innerClassName="p-5 border border-[rgba(255,77,109,.3)]">
        <div className="flex items-center gap-2 text-[var(--error,#ff4d6d)]"><Trash2 className="h-4 w-4" /><span className="font-display uppercase tracking-widest text-sm">Bookkeeping Reset (danger)</span></div>
        <p className="mt-2 text-sm text-[var(--text-muted)]">Wipes the chosen ledger to £0. Competitions &amp; users are kept. Irreversible — type <span className="font-mono text-white">RESET</span> to enable.</p>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--text-muted)]">Scope</label>
            <select value={resetScope} onChange={(e) => setResetScope(e.target.value)} data-testid="reset-scope" className="mt-1 block bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
              <option value="transactions">transactions</option><option value="expenses">expenses</option><option value="charity">charity</option><option value="all">all</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--text-muted)]">Type RESET</label>
            <input value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value.toUpperCase())} data-testid="reset-confirm" className="mt-1 block w-32 bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono" />
          </div>
          <NeonButton variant="outlinePink" onClick={doReset} loading={busy} disabled={resetConfirm !== "RESET"} data-testid="reset-button"><Trash2 className="h-4 w-4" /> Reset ledger</NeonButton>
        </div>
      </NeonCard>
    </div>
  );
}
